import React, { useState } from 'react'

/**
 * PriorityRow — a ranked entry. Large clay digit for #1, faint smaller
 * digit for #2 / #3. Optional `↳ goal` link. Status MP on the right.
 *
 * Phase E adds:
 *  - `onMoveUp` / `onMoveDown` callbacks — expose hover-reveal reorder grips
 *    and keyboard arrow-key reorder handlers (↑/↓ move the row within its list).
 *  - When either handler is provided, a reorder grip column appears on hover.
 *
 * Cards using this primitive should always pre-allocate 1–5 slots; never
 * show a lone "+ add" button. Hierarchy of priorities should be readable
 * at a glance — three equal items defeats the point.
 *
 * Spec: claude_design/design-system/primitives.html §8.3
 */
export function PriorityRow({
  rank,
  text,
  goal,
  status,
  empty = false,
  onMoveUp,
  onMoveDown,
  as: Tag = 'div',
  className = '',
  ...rest
}) {
  const [hovered, setHovered] = useState(false)
  const reorderable = !empty && (onMoveUp != null || onMoveDown != null)

  const rankClass = rank >= 2 ? `priorityrow--r${rank}` : ''
  const classes = ['priorityrow', rankClass, empty && 'priorityrow--empty', className]
    .filter(Boolean)
    .join(' ')

  function handleKeyDown(e) {
    if (e.key === 'ArrowUp' && onMoveUp) {
      e.preventDefault()
      onMoveUp()
    } else if (e.key === 'ArrowDown' && onMoveDown) {
      e.preventDefault()
      onMoveDown()
    }
  }

  return (
    <Tag
      className={classes}
      data-rank={rank}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...rest}
    >
      {reorderable && (
        <span
          className="priorityrow__grip"
          aria-hidden="true"
          style={{ opacity: hovered ? 0.5 : 0, transition: 'opacity var(--duration-fast) ease' }}
        >
          ⠿
        </span>
      )}
      <span
        className="priorityrow__rank"
        aria-hidden="true"
        tabIndex={reorderable ? 0 : undefined}
        onKeyDown={reorderable ? handleKeyDown : undefined}
        aria-label={reorderable ? `Priority ${rank}. Press up and down arrow keys to reorder.` : undefined}
      >
        {rank}
      </span>
      <div className="priorityrow__body">
        {empty ? (
          <div className="priorityrow__placeholder">—</div>
        ) : (
          <>
            {text && <div className="priorityrow__text">{text}</div>}
            {goal && <div className="priorityrow__goal">↳ {goal}</div>}
          </>
        )}
      </div>
      {status && <span className="priorityrow__status">{status}</span>}
    </Tag>
  )
}
